// components/formDialog/SoftProgramDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateSoftProgramMutation,
  useUpdateSoftProgramMutation,
} from "@/hooks/api/softProgram.queries";
import { batchService } from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import {
  SoftProgramCreateType,
  SoftProgramParamsType,
  SoftProgramType,
  softProgramService,
} from "@/services/softProgram.service";
import { studentService } from "@/services/student.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type SoftProgramDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: SoftProgramParamsType;
  programId?: string;
  programData?: SoftProgramType | null;
  onCancel: () => void;
};

const STATUS_OPTIONS = [
  { label: "Upcoming", value: "upcoming" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
];

const MODE_OPTIONS = [
  { label: "On-site", value: "On-site" },
  { label: "Online", value: "Online" },
  { label: "Hybrid", value: "Hybrid" },
];

const softProgramSchema = z.object({
  name: z.string().min(1, "Program Name is required"),
  vendor: z.string().min(1, "Vendor/Trainer is required"),
  skillsCovered: z.array(z.string()).min(1, "Select at least one skill"),
  students: z.array(z.string()).optional(),
  departments: z.array(z.string()).optional(),
  batches: z.array(z.string()).optional(),
  duration: z.string().min(1, "Duration is required"),
  maxParticipants: z.number().optional(),
  mode: z.enum(["On-site", "Online", "Hybrid"]),
  startDate: z.string().min(1, "Start Date is required"),
  endDate: z.string().optional(),
  status: z.enum(["upcoming", "ongoing", "completed"]),
  programObjectives: z.string().optional(),
  programDescription: z.string().optional(),
});

type SoftProgramFormType = z.infer<typeof softProgramSchema>;

const SoftProgramDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  programId,
  programData,
  onCancel,
}: SoftProgramDialogProps) => {
  const createMutation = useCreateSoftProgramMutation(filters);
  const updateMutation = useUpdateSoftProgramMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<SoftProgramFormType>({
    resolver: zodResolver(softProgramSchema),
    defaultValues: {
      name: "",
      vendor: "",
      skillsCovered: [],
      students: [],
      departments: [],
      batches: [],
      duration: "",
      maxParticipants: 0,
      mode: "On-site",
      startDate: "",
      endDate: "",
      status: "upcoming",
      programObjectives: "",
      programDescription: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && programData) {
        setTimeout(() => {
          form.reset({
            name: programData.name,
            vendor: programData.vendor,
            skillsCovered: programData.skillsCovered || [],
            students: programData.students || [],
            departments: programData.departments || [],
            batches: programData.batches || [],
            duration: programData.duration || "",
            maxParticipants: programData.maxParticipants || 0,
            mode: programData.mode || "On-site",
            startDate: programData.startDate || "",
            endDate: programData.endDate || "",
            status: programData.status || "upcoming",
            programObjectives: programData.programObjectives || "",
            programDescription: programData.programDescription || "",
          });
        }, 0);
      } else {
        form.reset({
          name: "",
          vendor: "",
          skillsCovered: [],
          students: [],
          departments: [],
          batches: [],
          duration: "",
          maxParticipants: 0,
          mode: "On-site",
          startDate: "",
          endDate: "",
          status: "upcoming",
          programObjectives: "",
          programDescription: "",
        });
      }
    }
  }, [mode, programData, isOpen, form]);

  const handleSubmit = (values: SoftProgramFormType) => {
    const submissionData: SoftProgramCreateType = {
      ...values,
      students: values.students || [],
      departments: values.departments || [],
      batches: values.batches || [],
      maxParticipants: values.maxParticipants || 0,
      endDate: values.endDate || "",
      programObjectives: values.programObjectives || "",
      programDescription: values.programDescription || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && programId) {
      updateMutation.mutate(
        { _id: programId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultVendorOptions = programData?.vendor
    ? [
        {
          label: `Vendor ${programData.vendor}`, // Fallback if data not fully joined
          value: programData.vendor,
        },
      ]
    : [];

  const defaultSkillsOptions =
    programData?.skillsCovered?.map((s) => ({
      label: s,
      value: s,
    })) || [];

  const defaultStudentOptions =
    programData?.students?.map((id) => ({
      label: `Student ${id}`,
      value: id,
    })) || [];

  const defaultDepartmentOptions =
    programData?.departments?.map((id) => ({
      label: `Dept ${id}`,
      value: id,
    })) || [];

  const defaultBatchOptions =
    programData?.batches?.map((id) => ({
      label: `Batch ${id}`,
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Soft Skills Program"
              : "Edit Soft Skills Program"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "add a new" : "edit the"} soft skills program
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Program Name */}
            <InputField<SoftProgramFormType>
              type="text"
              name="name"
              label="Program Name"
              placeholder="e.g., Leadership Development"
              required
              disabled={isPending}
            />

            {/* Vendor/Trainer */}
            <AsyncSingleSelectField<SoftProgramFormType>
              name="vendor"
              label="Vendor/Trainer"
              placeholder="Select vendor"
              fetchOptions={softProgramService.fetchSoftSkillVendorOptions}
              queryKey={["softSkillVendorsDrop"]}
              defaultOptions={defaultVendorOptions}
              required
              disabled={isPending}
            />

            {/* Skills Covered */}
            <AsyncMultiSelectField<SoftProgramFormType>
              name="skillsCovered"
              label="Skills Covered"
              placeholder="Select soft skills covered in this program"
              fetchOptions={softProgramService.fetchSoftSkillOptions}
              queryKey={["softSkills"]}
              defaultOptions={defaultSkillsOptions}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Enrolled Students */}
            <AsyncMultiSelectField<SoftProgramFormType>
              name="students"
              label="Enrolled Students"
              placeholder="Select students for this program"
              fetchOptions={studentService.fetchStudentOptions}
              queryKey={["students"]}
              defaultOptions={defaultStudentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Target Departments */}
            <AsyncMultiSelectField<SoftProgramFormType>
              name="departments"
              label="Target Departments"
              placeholder="Select departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
            />

            {/* Target Batches */}
            <AsyncMultiSelectField<SoftProgramFormType>
              name="batches"
              label="Target Batches"
              placeholder="Select batches"
              fetchOptions={batchService.fetchBatchOptions}
              queryKey={["batches"]}
              defaultOptions={defaultBatchOptions}
              disabled={isPending}
            />

            {/* 3-column Grid for Duration, Max Participants, Mode */}
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
              <InputField<SoftProgramFormType>
                type="text"
                name="duration"
                label="Duration"
                placeholder="e.g., 2 weeks"
                required
                disabled={isPending}
              />

              <InputField<SoftProgramFormType>
                type="number"
                name="maxParticipants"
                label="Max Participants"
                placeholder="e.g., 50"
                disabled={isPending}
              />

              <SelectField<SoftProgramFormType>
                name="mode"
                label="Mode"
                options={MODE_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* 3-column Grid for Start Date, End Date, Status */}
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
              <InputField<SoftProgramFormType>
                type="date"
                name="startDate"
                label="Start Date"
                required
                disabled={isPending}
              />

              <InputField<SoftProgramFormType>
                type="date"
                name="endDate"
                label="End Date"
                disabled={isPending}
              />

              <SelectField<SoftProgramFormType>
                name="status"
                label="Status"
                options={STATUS_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Program Objectives */}
            <TextareaField<SoftProgramFormType>
              name="programObjectives"
              label="Program Objectives"
              placeholder="Learning objectives and goals"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Program Description */}
            <TextareaField<SoftProgramFormType>
              name="programDescription"
              label="Program Description"
              placeholder="Brief description about the program"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Creating..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Program"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SoftProgramDialog;
